import { FC, Fragment } from 'react';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { Control, UseFormGetValues } from 'react-hook-form';
import { Column } from 'further-ui/components/Layout';
import { PeriodEndReportFormValues } from '../schema';
import { ToggleableFieldConfig } from './types';
import FieldGroup from './FieldGroup';
import ToggleableFieldRow from './ToggleableFieldRow';
import ReadOnlyDatapoint from './ReadOnlyDatapoint';

const generalFields: Array<ToggleableFieldConfig> = [
  {
    label: 'Fund name',
    valuePath: 'fundOverviewDatapoints.general.fundName.value',
    includedPath: 'fundOverviewDatapoints.general.fundName.included',
    readOnly: true,
  },
  {
    label: 'Fund currency',
    valuePath: 'fundOverviewDatapoints.general.fundCurrency.value',
    includedPath: 'fundOverviewDatapoints.general.fundCurrency.included',
    readOnly: true,
  },
  {
    label: 'Vintage year',
    valuePath: 'fundOverviewDatapoints.general.vintageYear.value',
    includedPath: 'fundOverviewDatapoints.general.vintageYear.included',
    readOnly: true,
  },
  {
    label: 'Display close dates',
    valuePath: 'fundOverviewDatapoints.general.displayCloseDate.value',
    includedPath: 'fundOverviewDatapoints.general.displayCloseDate.included',
    readOnly: true,
  },
  {
    label: 'Total commitments',
    valuePath: 'fundOverviewDatapoints.general.totalCommitments.value',
    includedPath: 'fundOverviewDatapoints.general.totalCommitments.included',
    readOnly: true,
  },
  {
    label: 'Display total commitments per close',
    valuePath:
      'fundOverviewDatapoints.general.displayTotalCommitmentsPerClose.value',
    includedPath:
      'fundOverviewDatapoints.general.displayTotalCommitmentsPerClose.included',
    readOnly: true,
  },
  {
    label: 'Fund term',
    valuePath: 'fundOverviewDatapoints.general.fundTerm.value',
    includedPath: 'fundOverviewDatapoints.general.fundTerm.included',
    placeholder: 'Enter fund term',
    readOnly: false,
  },
  {
    label: 'Legal form',
    valuePath: 'fundOverviewDatapoints.general.legalForm.value',
    includedPath: 'fundOverviewDatapoints.general.legalForm.included',
    placeholder: 'Enter legal form',
    readOnly: false,
  },
  {
    label: 'Formation jurisdiction',
    valuePath: 'fundOverviewDatapoints.general.formationJurisdiction.value',
    includedPath:
      'fundOverviewDatapoints.general.formationJurisdiction.included',
    readOnly: true,
  },
  {
    label: 'Investment adviser/manager',
    valuePath: 'fundOverviewDatapoints.general.investmentAdviserManager.value',
    includedPath:
      'fundOverviewDatapoints.general.investmentAdviserManager.included',
    placeholder: 'Enter adviser/manager',
    readOnly: false,
  },
  {
    label: 'General partner',
    valuePath: 'fundOverviewDatapoints.general.generalPartner.value',
    includedPath: 'fundOverviewDatapoints.general.generalPartner.included',
    placeholder: 'Enter general partner',
    readOnly: false,
  },
  {
    label: 'Reinvestment/recycling policy',
    valuePath:
      'fundOverviewDatapoints.general.reinvestmentRecyclingPolicy.value',
    includedPath:
      'fundOverviewDatapoints.general.reinvestmentRecyclingPolicy.included',
    placeholder: 'Enter reinvestment/recycling policy',
    readOnly: false,
  },
];

const OuterColumn = styled(Column)({
  maxWidth: '800px',
});

type Props = {
  control: Control<PeriodEndReportFormValues>;
  readOnly: boolean;
  getValues: UseFormGetValues<PeriodEndReportFormValues>;
};

const GeneralOverviewMetrics: FC<Props> = ({
  control,
  readOnly,
  getValues,
}) => {
  return (
    <OuterColumn>
      {readOnly ? (
        <Column spacing="sm">
          <Typography variant="h5" color="grey.900">
            Fund overview datapoints
          </Typography>
          {generalFields.map((config) => {
            const isIncluded = getValues(config.includedPath);
            const value = getValues(config.valuePath);

            return isIncluded && value ? (
              <ReadOnlyDatapoint
                key={config.valuePath}
                label={config.label}
                value={value}
              />
            ) : null;
          })}
        </Column>
      ) : (
        <Column spacing="sm">
          <Column spacing="xs">
            <Typography variant="h6">Fund overview datapoints</Typography>
            <Typography variant="body2">
              Select which of the following items you would like to include in
              your update.
            </Typography>
          </Column>
          <FieldGroup title="General overview metrics">
            {generalFields.map((config, index, array) => {
              const isNotLast = index < array.length - 1;

              return (
                <Fragment key={config.valuePath}>
                  <ToggleableFieldRow config={config} control={control} />
                  {isNotLast ? <Divider /> : null}
                </Fragment>
              );
            })}
          </FieldGroup>
        </Column>
      )}
    </OuterColumn>
  );
};

export default GeneralOverviewMetrics;
